// Shared consulting services configuration
// Used across admin-portal and frontend
// Update this file to automatically sync all locations

export const consultingServices = [
  {
    id: "financial-operations",
    label: "Financial and Operational Optimization Consulting",
    description:
      "Turn Operational Insights into Sustainable Financial and Operational Excellence",
    overview:
      "This service focuses on monitoring key financial and operational performance metrics and translating insights into practical actions that improve efficiency, margins, and long-term financial stability.",
    subcategories: [
      {
        id: "benchmarked-analysis",
        label: "Benchmarked Operational and Financial Metric Analysis",
        description:
          "Turn Operational Insights into Sustainable Financial and Operational Excellence",
        details:
          "We work with clients to establish clear financial and operational KPIs and benchmark performance to that of peer long-term care facilities. Clients gain improved visibility into performance, stronger cost control, more efficient operations, and improved margins.",
        outcome:
          "Enhanced financial stability, and a sustainable operating model that supports quality care.",
      },
    ],
  },
  {
    id: "strategic-growth",
    label: "Strategic Growth Consulting",
    description:
      "Support informed expansion through thoughtful planning, market insight, and evaluation",
    overview:
      "Our strategic growth consulting services support informed expansion through thoughtful planning, market insight, and evaluation of new service opportunities. We partner with long-term care leaders to identify, evaluate, and execute strategic growth opportunities aligned with market demand and organizational goals.",
    subcategories: [
      {
        id: "market-analysis",
        label: "Market Analysis",
        description:
          "Get actionable market insight to inform smarter long-term care decisions",
        details:
          "We analyze market demand, competitive dynamics, and demographic trends to help long-term care organizations understand their position and identify growth and risk factors. Our approach combines demographic and utilization data, competitive and referral-source analysis, and payer mix evaluation. We assess local and regional market conditions, regulatory factors, and emerging care trends to provide a clear picture of demand and opportunity.",
        outcome:
          "Evidence-based understanding of the market, clearer visibility into competitive positioning, and reliable data to support strategic, operational, and investment decisions.",
      },
      {
        id: "strategic-planning",
        label: "Strategic Planning",
        description:
          "Translate long-term care vision into an actionable growth strategy",
        details:
          "We help long-term care leaders define strategic priorities and develop practical, executable plans aligned with market realities and organizational capabilities. We facilitate stakeholder-engaged structured planning sessions with leadership, informed by market analysis and internal performance data. Together, we define strategic objectives, evaluate growth and improvement options, and translate priorities into a phased roadmap with clear initiatives, accountability, and timelines.",
        outcome:
          "Clear strategic direction, aligned leadership, and an actionable plan that supports sustainable growth and operational focus.",
      },
      {
        id: "feasibility-studies",
        label: "New Service Line Feasibility Studies",
        description:
          "Reduce risk and increase confidence in long-term care expansion decisions",
        details:
          "We help clients evaluate the financial, operational, and market viability of new or expanded service lines to support informed investment and growth decisions. Our feasibility studies assess market demand, competitive landscape, regulatory requirements, staffing implications, and financial performance. We model startup and operating costs, reimbursement scenarios, and break-even timelines to determine overall viability and risk.",
        outcome:
          "Data-backed recommendations, resulting in smarter investments, reduced risk, and stronger long-term returns.",
      },
    ],
  },
  {
    id: "grants",
    label: "Grants Writing and Management Services",
    description:
      "Identify, secure, and manage grant funding aligned with organizational priorities",
    overview:
      "Our comprehensive grant services help organizations identify high-value funding opportunities, develop compelling proposals, and manage grants from award through completion—ensuring maximum impact and compliance.",
    subcategories: [
      {
        id: "grants-prospecting",
        label: "Grants Prospecting",
        description:
          "Identify the right grant opportunities to support long-term care priorities",
        details:
          "We help clients identify grant opportunities aligned with their mission, programs, and strategic objectives. Our prospecting services focus on federal, state, foundation, and industry-specific funding sources relevant to healthcare operations, workforce initiatives, capital improvements, and program development. We begin by understanding your organizational goals, service offerings, and funding needs. We then research and screen grant opportunities using established funding databases and sector-specific sources, evaluating eligibility criteria, funding history, timelines, and alignment with your capabilities. Findings are delivered in a clear, prioritized funding pipeline with recommended next steps. By filtering opportunities based on eligibility, competitiveness, funding purpose, and reporting requirements, we help leadership focus time and resources on grants with the highest likelihood of success and organizational fit.",
        outcome:
          "Gain a targeted list of high-value grant opportunities, reduced time spent on low-probability applications, and a clearer funding strategy aligned with organizational priorities.",
      },
      {
        id: "proposal-development",
        label: "Proposal Development",
        description: "Develop compelling, compliant grant proposals",
        details:
          "We provide end-to-end grant proposal development services, translating program concepts, operational needs, and outcomes into clear, persuasive proposals. Our work ensures applications are compliant, well-structured, and aligned with funder priorities. We collaborate closely with leadership and program teams to capture your organization's story, impact, and readiness to execute, while maintaining a professional, data-driven narrative appropriate for public and private funders. Our process includes reviewing funder guidelines, outlining proposal structure, drafting narrative sections, developing budgets and supporting documentation, and coordinating internal inputs.",
        outcome: "High quality proposals and improved funding success",
      },
      {
        id: "proposal-editing",
        label: "Grant Proposal Editing and Refinement",
        description:
          "Get a second look at your already drafted grant proposals",
        details:
          "We provide professional editing and refinement services for draft grant proposals prepared internally or by third parties. Our focus is on improving clarity, consistency, and alignment with funder priorities while ensuring compliance with all application requirements. This service is ideal for organizations with internal grant capacity that want an experienced external review to elevate proposal quality and reduce avoidable weaknesses. We conduct a detailed review of draft narratives, budgets, and attachments against funder criteria. Edits focus on strengthening alignment, sharpening outcomes and evaluation language, clarifying financial and operational readiness, and ensuring a cohesive, compelling narrative throughout the application.",
        outcome: "High quality proposals and improved funding success",
      },
      {
        id: "grant-management",
        label: "Grant Management Services",
        description:
          "Ensure grant compliance, reporting, and performance from award to closeout",
        details:
          "We support clients with non-financial management post-award grant management to ensure compliance with funder requirements and effective use of awarded funds. Our services help organizations manage reporting, documentation, and performance tracking without overburdening operational teams. We establish grant tracking tools, reporting calendars, and compliance checklists. We assist with progress reporting, documentation management, and coordination between program, finance, and leadership teams. Support may be ongoing or tied to specific reporting cycles.",
        outcome:
          "Maintain full compliance, submit timely and accurate grant reports, and demonstrate program impact",
      },
      {
        id: "grants-management-bundle",
        label: "Grants Management Bundle",
        description:
          "End-to-end grant services from prospecting through post-award management",
        bundle: true,
        includes: [
          "Grants Prospecting",
          "Proposal Development",
          "Grant Management Services",
        ],
        note: "12.5% of grant award · $4,500 deposit credited back at award",
        defaultBillingModel: "percent_of_grant_award",
        defaultRate: "12.5",
        bundleTerms: {
          type: "grants_management_bundle",
          depositAmount: "4500",
          signingPercentOfBundle: "25",
          depositCreditedOnSuccess: true,
          notes:
            "Prospecting + Proposal Development + Post-Award Grants Management",
          includedServices:
            "Prospecting, full proposal development, and post-award grants management",
        },
      },
    ],
  },
  {
    id: "workforce",
    label: "Workforce Retention Consulting",
    description:
      "Reduce turnover, prevent burnout, and build a resilient, engaged workforce",
    overview:
      "Our Workforce Retention Consulting service helps Long-Term Care organizations reduce turnover, prevent burnout, and build a resilient, engaged workforce. Through data-driven assessments, frontline insights, and practical retention strategies, we address the real drivers of disengagement—before they lead to vacancies. The result is stronger teams, improved staff morale, and consistent, high-quality resident care.",
    subcategories: [
      {
        id: "engagement-assessment",
        label: "Staff Engagement & Satisfaction Assessments",
        description: "Understand What Truly Drives Your Workforce",
        details:
          "Our Staff Engagement & Satisfaction Assessment provides Long-Term Care (LTC) leaders with a clear, data-driven understanding of how employees experience their work environment—across leadership communication, workload balance, recognition, growth opportunities, and morale. Unlike generic employee surveys, our LTC-specific assessment approach captures the realities of caregiving roles, shift-based work, and frontline pressures. We translate staff feedback into clear, actionable insights that leadership can immediately use to strengthen engagement, improve retention, and enhance resident care outcomes.",
        phases: [
          "Phase 1: Planning & Alignment - Leadership kickoff, role mapping, and assessment design aligned to organizational goals",
          "Phase 2: Data Collection - Collection of qualitative and quantitative data through surveys, interviews, and focus groups",
          "Phase 3: Analysis & Insight Generation - Data analysis and theme extraction, stratified by department and role",
          "Phase 4: Reporting & Action Planning - Executive and operational reporting, prioritization of recommendations, and development of a practical action plan",
          "Phase 5: Implementation, Monitoring & Evaluation - Action plan deployment, pulse monitoring, continuous optimization, and scaling of proven improvements",
        ],
        outcome:
          "Higher engagement, stronger morale, improved retention, and a more stable care workforce.",
      },
      {
        id: "burnout-assessment",
        label: "Burnout Risk Assessment",
        description: "Identify Burnout Before It Becomes Turnover",
        details:
          "Burnout is one of the leading drivers of turnover in Long-Term Care—and it often goes unnoticed until staffing stability is already at risk. Our Burnout Risk Assessment helps organizations proactively identify stress points, fatigue patterns, and systemic drivers of burnout across clinical and support teams. By integrating validated burnout assessment tools, workload analysis, and direct staff input, we pinpoint where burnout is emerging—and recommend targeted, practical interventions before disengagement leads to resignations. This service equips leadership with the insight needed to protect staff well-being, reduce absenteeism, and stabilize workforce capacity.",
        deliverables: [
          "Burnout risk profile by role and department",
          "Identification of operational and cultural stressors",
          "Early-intervention recommendations tailored to your organization",
        ],
        outcome:
          "Reduced burnout-driven turnover, healthier teams, and sustained workforce performance.",
      },
      {
        id: "retention-strategy",
        label: "Workforce Retention Strategy Development & Deployment",
        description: "Turn Retention into a Competitive Advantage",
        details:
          "Our Workforce Retention Strategy service helps LTC organizations move from reactive staffing solutions to long-term workforce stability. Using real workforce data and frontline insights, we partner with leadership to design customized retention strategies that address culture, career pathways, compensation alignment, leadership practices, and the overall employee experience. Our approach goes beyond planning—we support execution and ongoing monitoring to ensure strategies translate into measurable results.",
        process: [
          "Workforce retention diagnostics",
          "Strategically aligned retention planning",
          "Implementation support and leadership enablement",
          "Performance monitoring and refinement",
        ],
        outcome:
          "Fewer vacancies, stronger teams, reduced recruitment costs, and a more resilient care organization.",
      },
      {
        id: "onboarding-training",
        label:
          "Workforce Onboarding & Training: Customer Service & Resident Experience",
        description:
          "Build Confident Staff Who Deliver Exceptional Resident Experiences",
        details:
          "Our Workforce Onboarding & Training program equips LTC staff with the skills, mindset, and behaviors needed to consistently deliver compassionate, resident-centered care. We focus on customer service excellence, communication, professionalism, and empathy—ensuring every staff interaction reinforces trust, dignity, and quality. We begin with a training needs assessment, then design and deploy a customized curriculum aligned with organizational values, regulatory expectations, and quality goals. The program accelerates new-hire confidence, strengthens team culture, and improves resident satisfaction from day one.",
        outcome:
          "Faster onboarding, higher staff confidence, improved resident experience, and a stronger organizational culture.",
      },
      {
        id: "workforce-consulting-bundle",
        label: "Workforce Consulting Bundle",
        description:
          "Comprehensive workforce assessment and retention strategy package",
        bundle: true,
        includes: [
          "Staff Engagement & Satisfaction Assessments",
          "Burnout Risk Assessment",
          "Workforce Retention Strategy Development & Deployment",
        ],
        note: "Save $7,500 vs. individual pricing ($27,500)",
        defaultBillingModel: "fixed",
        defaultRate: "20000",
        bundleTerms: {
          type: "package_flat",
          notes: "Staff Engagement + Burnout + Retention Strategy bundle",
          includedServices:
            "Staff Engagement Assessment, Burnout Assessment, and Workforce Retention Strategy Development",
        },
      },
    ],
  },
];

// Transform for admin portal (subcategories format)
export const getConsultingSubcategories = () => {
  return consultingServices.map((service) => ({
    value: service.id,
    label: service.label,
    subcategories: service.subcategories,
  }));
};

// Transform for public website (serviceCategories format)
export const getServiceCategories = () => {
  return consultingServices.map((category) => ({
    title: category.label,
    description: category.description,
    subcategories: category.subcategories,
  }));
};

const flatConsultingSubcategories = consultingServices.flatMap((category) =>
  (category.subcategories || []).map((subcategory) => ({
    categoryId: category.id,
    categoryLabel: category.label,
    subcategoryId: subcategory.id,
    subcategoryLabel: subcategory.label,
  })),
);

export const findConsultingSubcategory = (value) => {
  const needle = String(value || "")
    .trim()
    .toLowerCase();
  if (!needle) return null;
  return (
    flatConsultingSubcategories.find(
      (item) =>
        item.subcategoryId?.toLowerCase() === needle ||
        item.subcategoryLabel?.toLowerCase() === needle,
    ) || null
  );
};

export const deriveConsultingCategories = (values = []) => {
  const categories = new Set();
  (values || []).forEach((value) => {
    const match = findConsultingSubcategory(value);
    if (match?.categoryLabel) categories.add(match.categoryLabel);
  });
  return Array.from(categories);
};
